import { Injectable, computed, signal } from '@angular/core';
import { Filters, DEFAULT_FILTERS } from '../models';
import { ObjectSearchFiltersDto, PetPolicyFilter } from '../api/models';
import { BoundingBox, Coordinates } from '@konradkrk/ngx-map-picker';

export interface MapViewport {
  center: Coordinates;
  zoom: number;
}

export const DEFAULT_MAP_VIEWPORT: MapViewport = {
  center: { lat: 51.9, lng: 19.1 },
  zoom: 6,
};

@Injectable({ providedIn: 'root' })
export class FiltersService {
  private readonly _filters = signal<Filters>({ ...DEFAULT_FILTERS });
  private readonly _showFiltersModal = signal(false);
  private readonly _maxPriceRange = signal(100000); // 1000 zł default, updated from API
  private readonly _mapBounds = signal<BoundingBox | null>(null);
  private readonly _mapViewport = signal<MapViewport>({ ...DEFAULT_MAP_VIEWPORT });

  readonly filters = this._filters.asReadonly();
  readonly showFiltersModal = this._showFiltersModal.asReadonly();
  readonly maxPriceRange = this._maxPriceRange.asReadonly();
  readonly mapBounds = this._mapBounds.asReadonly();
  readonly mapViewport = this._mapViewport.asReadonly();

  /**
   * Build ObjectSearchFiltersDto from current filter state.
   * Prices in filters are in ZŁ — converted to grosze for API.
   */
  readonly searchFilters = computed((): ObjectSearchFiltersDto => {
    const f = this._filters();
    const bounds = this._mapBounds();
    const dto: ObjectSearchFiltersDto = {
      page: 1,
      limit: 20,
    };

    if (bounds) {
      dto.minLat = bounds.south;
      dto.maxLat = bounds.north;
      dto.minLng = bounds.west;
      dto.maxLng = bounds.east;
    }

    if (f.search) dto.search = f.search;
    if (f.city) dto.city = f.city;
    if (f.guests > 0) dto.minCapacity = f.guests;
    if (f.propertyTypeIds.length > 0) dto.propertyTypeIds = f.propertyTypeIds;
    if (f.priceMin != null && f.priceMin > 0) dto.minPrice = f.priceMin * 100; // zł → grosze
    if (f.priceMax != null && f.priceMax > 0 && f.priceMax < this._maxPriceRange() / 100) {
      dto.maxPrice = f.priceMax * 100; // zł → grosze
    }
    if (f.petPolicy) dto.petPolicy = f.petPolicy;
    if (f.minStay) dto.minStay = f.minStay;
    if (f.amenityIds.length > 0) dto.amenityIds = f.amenityIds;
    if (f.sortBy) dto.sortBy = f.sortBy;
    if (f.sortOrder) dto.sortOrder = f.sortOrder;
    if (f.dateFrom) dto.dateFrom = this.formatDate(f.dateFrom);
    if (f.dateTo) dto.dateTo = this.formatDate(f.dateTo);

    return dto;
  });

  /**
   * Build filters for GET /public/objects/map — same filter state as
   * searchFilters but WITHOUT bounding box, pagination and sorting:
   * the map loads all matching offers once and renders them client-side.
   */
  readonly mapFilters = computed((): ObjectSearchFiltersDto => {
    const f = this._filters();
    const dto: ObjectSearchFiltersDto = {};

    if (f.search) dto.search = f.search;
    if (f.city) dto.city = f.city;
    if (f.guests > 0) dto.minCapacity = f.guests;
    if (f.propertyTypeIds.length > 0) dto.propertyTypeIds = f.propertyTypeIds;
    if (f.priceMin != null && f.priceMin > 0) dto.minPrice = f.priceMin * 100; // zł → grosze
    if (f.priceMax != null && f.priceMax > 0 && f.priceMax < this._maxPriceRange() / 100) {
      dto.maxPrice = f.priceMax * 100; // zł → grosze
    }
    if (f.petPolicy) dto.petPolicy = f.petPolicy;
    if (f.minStay) dto.minStay = f.minStay;
    if (f.amenityIds.length > 0) dto.amenityIds = f.amenityIds;
    if (f.dateFrom) dto.dateFrom = this.formatDate(f.dateFrom);
    if (f.dateTo) dto.dateTo = this.formatDate(f.dateTo);

    return dto;
  });

  readonly activeFiltersCount = computed(() => {
    const f = this._filters();
    let count = 0;

    if (f.petPolicy) count++;
    if (f.minStay) count++;
    if (f.priceMin != null && f.priceMin > 0) count++;
    if (f.priceMax != null && f.priceMax > 0 && f.priceMax < this._maxPriceRange() / 100) count++;
    count += f.amenityIds.length;

    return count;
  });

  setMapBounds(bounds: BoundingBox): void {
    this._mapBounds.set(bounds);
  }

  setMapViewport(center: Coordinates, zoom: number): void {
    this._mapViewport.set({ center, zoom });
  }

  setMaxPriceRange(maxGrosze: number): void {
    this._maxPriceRange.set(maxGrosze);
    // Set default max if not yet set
    const f = this._filters();
    if (f.priceMax == null) {
      this._filters.update((current) => ({
        ...current,
        priceMax: Math.ceil(maxGrosze / 100),
      }));
    }
  }

  updateFilters(partialFilters: Partial<Filters>): void {
    this._filters.update((current) => ({
      ...current,
      ...partialFilters,
    }));
  }

  setSearch(search: string): void {
    this.updateFilters({ search });
  }

  setCity(city: string): void {
    this.updateFilters({ city });
  }

  togglePropertyType(typeId: number): void {
    this._filters.update((current) => {
      const ids = current.propertyTypeIds.includes(typeId)
        ? current.propertyTypeIds.filter((id) => id !== typeId)
        : [...current.propertyTypeIds, typeId];
      return { ...current, propertyTypeIds: ids };
    });
  }

  setPropertyTypeIds(ids: number[]): void {
    this.updateFilters({ propertyTypeIds: ids });
  }

  setGuests(guests: number): void {
    this.updateFilters({ guests });
  }

  setAdults(adults: number): void {
    this.updateFilters({ adults, guests: adults + this._filters().children });
  }

  setChildren(children: number): void {
    this.updateFilters({ children, guests: this._filters().adults + children });
  }

  setWithPets(withPets: boolean): void {
    this.updateFilters({ withPets });
  }

  setPriceRange(min: number | null, max: number | null): void {
    this.updateFilters({ priceMin: min, priceMax: max });
  }

  setPetPolicy(petPolicy: PetPolicyFilter | null): void {
    this.updateFilters({ petPolicy });
  }

  setMinStay(minStay: number | null): void {
    this.updateFilters({ minStay });
  }

  setAmenityIds(amenityIds: number[]): void {
    this.updateFilters({ amenityIds });
  }

  toggleAmenity(amenityId: number): void {
    this._filters.update((current) => {
      const amenityIds = current.amenityIds.includes(amenityId)
        ? current.amenityIds.filter((id) => id !== amenityId)
        : [...current.amenityIds, amenityId];
      return { ...current, amenityIds };
    });
  }

  setSortBy(sortBy: string): void {
    this.updateFilters({ sortBy });
  }

  setSortOrder(sortOrder: 'ASC' | 'DESC'): void {
    this.updateFilters({ sortOrder });
  }

  setDates(dateFrom: Date | null, dateTo: Date | null): void {
    this.updateFilters({ dateFrom, dateTo });
  }

  clearDates(): void {
    this.updateFilters({ dateFrom: null, dateTo: null });
  }

  resetFilters(): void {
    this._filters.set({ ...DEFAULT_FILTERS });
  }

  /**
   * Reset tylko pól widocznych w modalu filtrów (cena, minimalny pobyt,
   * udogodnienia). NIE czyści lokalizacji, dat, gości ani sortowania — inaczej
   * "Wyczyść filtry" po cichu kasowałby kontekst wyszukiwania spoza modalu.
   */
  resetModalFilters(): void {
    this.updateFilters({
      priceMin: DEFAULT_FILTERS.priceMin,
      priceMax: DEFAULT_FILTERS.priceMax,
      minStay: DEFAULT_FILTERS.minStay,
      amenityIds: [],
      petPolicy: DEFAULT_FILTERS.petPolicy,
    });
  }

  openFiltersModal(): void {
    this._showFiltersModal.set(true);
  }

  closeFiltersModal(): void {
    this._showFiltersModal.set(false);
  }

  toggleFiltersModal(): void {
    this._showFiltersModal.update((v) => !v);
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
